import type { Locale } from "../redux/languageSlice";

/**
 * Helpers for reading the bilingual fields stored on database records.
 *
 * Convention (matches the backend schema): every translatable field keeps its
 * English value on the base key and its Arabic value on a `…Ar` sibling key —
 * `title` / `titleAr`, `description` / `descriptionAr`, and so on. English is
 * the canonical, always-present value; the Arabic sibling is optional.
 *
 * The API returns *both* languages on every response so switching language is a
 * pure client-side re-render with no refetch.
 */

/** Treat whitespace-only translations as missing. */
const isFilled = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Pick the value for the active locale, falling back to the other language when
 * the requested one has not been translated yet. Never returns `undefined`, so
 * a partly-translated course still renders instead of showing a blank line.
 */
export const pickLocalized = (
  en: string | undefined | null,
  ar: string | undefined | null,
  locale: Locale
): string => {
  if (locale === "ar") return isFilled(ar) ? ar.trim() : isFilled(en) ? en.trim() : "";
  return isFilled(en) ? en.trim() : isFilled(ar) ? ar.trim() : "";
};

/** True when the Arabic translation for this field is actually present. */
export const hasArabic = (ar?: string | null): boolean => isFilled(ar);

/** `pickLocalized` for a record that follows the `field` / `fieldAr` convention. */
export const localizedField = <T extends Record<string, any>>(
  record: T | null | undefined,
  field: keyof T & string,
  locale: Locale
): string => {
  if (!record) return "";
  return pickLocalized(record[field], record[`${field}Ar`], locale);
};

/** Shorthand for the two fields read most often across the course screens. */
export const localizedTitle = <T extends { title?: string; titleAr?: string }>(
  record: T | null | undefined,
  locale: Locale
): string => pickLocalized(record?.title, record?.titleAr, locale);

export const localizedDescription = <T extends { description?: string; descriptionAr?: string }>(
  record: T | null | undefined,
  locale: Locale
): string => pickLocalized(record?.description, record?.descriptionAr, locale);

/**
 * Search haystack for a bilingual record — matches whichever language the user
 * types in, regardless of the interface language they are currently viewing.
 */
export const bilingualSearchText = (...values: (string | undefined | null)[]): string =>
  values.filter(isFilled).join(" ").toLowerCase();
